import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CalendarDots, Users, Plus, Trash, MagnifyingGlass, User } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dayColors = {
  'Segunda': '#E85D04',
  'Terca': '#0077B6',
  'Quarta': '#2D6A4F',
  'Quinta': '#9B2226',
  'Sexta': '#6A4C93',
  'Sabado': '#E85D04',
};

export default function TurmasFixas() {
  const [turmas, setTurmas] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTurma, setSelectedTurma] = useState(null);
  const [addStudentId, setAddStudentId] = useState('');
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [turmasRes, studentsRes] = await Promise.all([
        axios.get(`${API}/turmas/alunos`, { withCredentials: true }),
        axios.get(`${API}/students`, { withCredentials: true })
      ]);
      setTurmas(turmasRes.data);
      setStudents(studentsRes.data);
    } catch (e) {
      toast.error('Erro ao carregar turmas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const assignStudent = async (studentId, turmaId, override = false) => {
    try {
      await axios.post(`${API}/turmas/assign`, {
        student_id: studentId,
        turma_id: turmaId,
        action: 'add',
        override,
      }, { withCredentials: true });
      toast.success('Aluno adicionado na turma');
      setAddStudentId('');
      fetchData();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao adicionar aluno';
      // offer override if limit hit
      if (!override && msg.includes('override')) {
        if (window.confirm(`${msg}\n\nDeseja adicionar mesmo assim (override admin)?`)) {
          return assignStudent(studentId, turmaId, true);
        }
      } else {
        toast.error(msg);
      }
    }
  };

  const removeStudent = async (studentId, turmaId) => {
    try {
      await axios.post(`${API}/turmas/assign`, {
        student_id: studentId,
        turma_id: turmaId,
        action: 'remove',
      }, { withCredentials: true });
      toast.success('Aluno removido da turma');
      fetchData();
    } catch (e) {
      toast.error('Erro ao remover aluno');
    }
  };

  const broadcastEmail = async () => {
    if (!window.confirm('Enviar email para TODOS os alunos ativos pedindo para atualizarem as turmas?')) return;
    try {
      const { data } = await axios.post(`${API}/admin/email-update-turmas`, {}, { withCredentials: true });
      toast.success(`Email enviado para ${data.sent} aluno(s)${data.errors ? ` (${data.errors} falhas)` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar emails');
    }
  };

  const days = ['Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];
  const grouped = {};
  days.forEach(d => { grouped[d] = turmas.filter(t => t.dia === d); });

  const isStudentInAnyTurma = (s) => {
    if (Array.isArray(s.turmas) && s.turmas.length > 0) return true;
    return !!s.turma;
  };
  const unassigned = students.filter(s => !isStudentInAnyTurma(s) && s.status === 'ativo');
  const totalStudents = turmas.reduce((sum, t) => sum + t.count, 0);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="turmas-page">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="page-title">Turmas Fixas</h1>
            <p className="text-[#73706A] mt-1">Grade de horarios e alunos por turma. Alunos podem estar em mais de uma turma.</p>
          </div>
          <Button
            onClick={broadcastEmail}
            variant="outline"
            className="border-[#E85D04] text-[#E85D04] hover:bg-orange-50"
            data-testid="broadcast-update-turmas-btn"
          >
            Enviar email pedindo atualizacao
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="stat-card">
            <p className="text-sm text-[#73706A]">Total de Turmas</p>
            <p className="text-2xl font-semibold text-[#1A1A1A] font-['Outfit']">{turmas.length}</p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-[#73706A]">Alunos nas Turmas</p>
            <p className="text-2xl font-semibold text-[#2D6A4F] font-['Outfit']">{totalStudents}</p>
          </div>
          <div className="stat-card">
            <p className="text-sm text-[#73706A]">Sem Turma</p>
            <p className="text-2xl font-semibold text-[#E85D04] font-['Outfit']">{unassigned.length}</p>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-[#E85D04] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div className="space-y-6">
            {days.map(day => {
              const dayTurmas = grouped[day];
              if (!dayTurmas || dayTurmas.length === 0) return null;
              const color = dayColors[day] || '#73706A';
              return (
                <div key={day} className="bg-white border border-[#E6E2D8] rounded-lg overflow-hidden">
                  <div className="px-6 py-3 flex items-center gap-3" style={{ backgroundColor: color }}>
                    <CalendarDots size={20} className="text-white" />
                    <h2 className="font-['Outfit'] font-semibold text-white text-lg">{day}</h2>
                  </div>
                  <div className="divide-y divide-[#E6E2D8]">
                    {dayTurmas.map((turma) => (
                      <div key={turma.id} className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-bold font-['Outfit']" style={{ color }}>{turma.horario}</span>
                            <span className="font-medium text-[#1A1A1A]">{turma.turma}</span>
                            <span className="badge badge-info text-xs">{turma.count} aluno(s)</span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedTurma(selectedTurma === turma.id ? null : turma.id)}
                            className="text-xs"
                            data-testid={`toggle-turma-${turma.id}`}
                          >
                            {selectedTurma === turma.id ? 'Fechar' : 'Gerenciar'}
                          </Button>
                        </div>

                        {turma.students.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {turma.students.map(s => (
                              <div key={s.id} className="flex items-center gap-1 bg-[#F5F2EB] rounded-full px-3 py-1">
                                <User size={12} className="text-[#73706A]" />
                                <span className="text-xs text-[#1A1A1A]">{s.name}</span>
                                {selectedTurma === turma.id && (
                                  <button onClick={() => removeStudent(s.id, turma.id)} className="text-[#9B2226] hover:text-red-700 ml-1">
                                    <Trash size={12} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {selectedTurma === turma.id && (
                          <div className="mt-3 p-3 bg-[#F5F2EB] rounded-lg">
                            <p className="text-xs text-[#73706A] mb-2">Adicionar aluno na turma:</p>
                            <div className="flex gap-2">
                              <Select value={addStudentId} onValueChange={setAddStudentId}>
                                <SelectTrigger className="flex-1" data-testid={`add-student-select-${turma.id}`}>
                                  <SelectValue placeholder="Selecione um aluno" />
                                </SelectTrigger>
                                <SelectContent>
                                  {students.filter(s => {
                                    if (s.status !== 'ativo') return false;
                                    const inThis = Array.isArray(s.turmas)
                                      ? s.turmas.includes(turma.id)
                                      : s.turma === turma.id;
                                    return !inThis;
                                  }).map(s => {
                                    const count = Array.isArray(s.turmas) ? s.turmas.length : (s.turma ? 1 : 0);
                                    return (
                                      <SelectItem key={s.id} value={s.id}>
                                        {s.name} {count > 0 ? `(${count} turma${count > 1 ? 's' : ''})` : ''}
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white"
                                disabled={!addStudentId}
                                onClick={() => assignStudent(addStudentId, turma.id)}
                                data-testid={`add-student-btn-${turma.id}`}
                              >
                                <Plus size={16} />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {unassigned.length > 0 && (
              <div className="bg-white border border-[#E6E2D8] rounded-lg p-6">
                <h3 className="font-['Outfit'] font-semibold text-[#E85D04] mb-3 flex items-center gap-2">
                  <Users size={20} /> Alunos sem turma ({unassigned.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {unassigned.map(s => (
                    <span key={s.id} className="bg-orange-50 border border-orange-200 rounded-full px-3 py-1 text-xs text-[#1A1A1A]">
                      {s.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
